import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Sparkline from "@/components/Sparkline";
import CustomerFormDialog from "@/components/CustomerFormDialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  DownloadSimple,
  UploadSimple,
  Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Potential", "Engaged", "Negotiation", "Converted", "Lost"];

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [owner, setOwner] = useState("all");
  const [owners, setOwners] = useState([]);
  const [sort, setSort] = useState("updated_at");
  const [mineOnly, setMineOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sparklines, setSparklines] = useState({});
  const fileInputRef = useRef(null);

  const loadSparklines = useCallback(async () => {
    try {
      const { data } = await api.get("/customers/activity-sparklines", {
        params: { weeks: 12 },
      });
      setSparklines(data || {});
    } catch (err) {
      console.error("Failed to load sparklines:", err);
    }
  }, []);

  const loadOwners = useCallback(async () => {
    try {
      const { data } = await api.get("/customers/owners");
      setOwners(data.owners || []);
    } catch (err) {
      console.error("Failed to load owners:", err);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", {
        params: {
          search: search || undefined,
          status,
          owner,
          sort,
          mine: mineOnly || undefined,
        },
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [search, status, owner, sort, mineOnly]);

  useEffect(() => {
    // eslint-disable-next-line
    loadOwners();
    loadSparklines();
  }, [loadOwners, loadSparklines]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleCreated = (c) => {
    setShowForm(false);
    toast.success("Customer created");
    setRows((r) => [c, ...r]);
    loadOwners();
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `svraf-customers-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/customers/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} customer(s)`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} row(s) skipped`);
      }
      load();
      loadOwners();
    } catch {
      toast.error("Import failed — check CSV format");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="customers-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Pipeline
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Customers
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImportFile}
              data-testid="import-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              data-testid="import-csv-button"
            >
              <UploadSimple size={16} weight="bold" />
              {importing ? "Importing…" : "Import CSV"}
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
              data-testid="export-csv-button"
            >
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
            <button
              onClick={() => setShowForm(true)}
              data-testid="new-customer-button"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} weight="bold" /> New Customer
            </button>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-5">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid="customer-search-input"
              placeholder="Search by name, company, contact email…"
              className="pl-9 h-10 bg-white border-slate-200"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="w-full lg:w-44 h-10 bg-white border-slate-200"
              data-testid="status-filter-select"
            >
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="status-filter-all">
                All statuses
              </SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-filter-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger
              className="w-full lg:w-56 h-10 bg-white border-slate-200"
              data-testid="owner-filter-select"
            >
              <SelectValue placeholder="Filter by SVRAF PoC" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SVRAF PoCs</SelectItem>
              {owners.length === 0 && (
                <div className="px-2 py-1.5 text-xs text-slate-400">
                  No PoCs assigned
                </div>
              )}
              {owners.map((o) => (
                <SelectItem key={o} value={o} data-testid={`owner-filter-${o}`}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              className="w-full lg:w-44 h-10 bg-white border-slate-200"
              data-testid="sort-select"
            >
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_at">Last updated</SelectItem>
              <SelectItem value="created_at">Newest</SelectItem>
              <SelectItem value="name">Name (A–Z)</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={() => setMineOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-sm font-medium transition-colors ${
              mineOnly
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="mine-only-toggle"
            title={mineOnly ? "Showing only records I created" : "Show all records"}
          >
            <span className={`w-2 h-2 rounded-full ${mineOnly ? "bg-blue-600" : "bg-slate-300"}`} />
            Mine only
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Company</th>
                  <th className="px-6 py-3">SVRAF PoC</th>
                  <th className="px-6 py-3">Value</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">12-wk activity</th>
                  <th className="px-6 py-3">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      No customers match your filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/sales/customers/${c.id}`)}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      data-testid={`customer-row-${c.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.email || "—"}</div>
                        {c.created_by_email && (
                          <div
                            className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[180px]"
                            title={`Created by ${c.created_by_email}`}
                            data-testid={`created-by-${c.id}`}
                          >
                            by {c.created_by_email.split("@")[0]}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-700">{c.company || "—"}</td>
                      <td className="px-6 py-4">
                        {(() => {
                          const pocs = c.svraf_pocs || [];
                          const primary = pocs.find((p) => p.is_primary) || pocs[0];
                          const others = pocs.filter((p) => p !== primary);
                          if (!primary) {
                            return (
                              <span className="text-slate-400 italic text-xs">
                                Unassigned
                              </span>
                            );
                          }
                          return (
                            <div className="flex items-center gap-1.5">
                              <Star size={12} weight="fill" className="text-amber-500 shrink-0" />
                              <div className="leading-tight">
                                <div className="text-slate-800 font-medium">
                                  {primary.name}
                                </div>
                                {others.length > 0 && (
                                  <div className="text-xs text-slate-500">
                                    + {others.length} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-slate-800 font-medium">
                        {c.opportunity_value > 0 ? (
                          <span data-testid={`customer-value-${c.id}`}>
                            ₹{Number(c.opportunity_value).toLocaleString("en-IN")}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="px-6 py-4" data-testid={`customer-sparkline-${c.id}`}>
                        <Sparkline data={sparklines[c.id] || Array(12).fill(0)} />
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-xs">
                        {new Date(c.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showForm && (
        <CustomerFormDialog
          open={showForm}
          onClose={() => setShowForm(false)}
          onSaved={handleCreated}
        />
      )}
    </div>
  );
}
